import './QuickAccessViews.scss';

import { coreActions } from '@sonar-software/sonar-core';
import { getSonarObjectTypeFromId } from '@sonar-software/sonar-ts-lib';
import { MenuInfo } from 'rc-menu/lib/interface';
import { QuickAccessViewButton } from './QuickAccessViewButton';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { Button, Menu } from 'antd';
import { COPY_GENERIC, COPY_SPECIFIC, DATA_DICTIONARY_COPY, LINKS } from '@sonar-software/sonar-ts-constants';
import React, { useCallback, useMemo } from 'react';
import { SearchGroup, SearchOptionGroup } from './DataDictionarySearch';
import { useAction, useBoolean, useCustomViews, useDataSecurityApps, useOnMount } from '@hooks';

const viewsGroup = {
	key: 'title',
	label: COPY_SPECIFIC.SAVED_VIEWS,
	type: 'group'
};

export interface QuickAccessViewSelect {
	applicationId?: number;
	complianceCategory?: number;
	displayName?: string;
	sonarObjectId?: number;
	sonarObjectType?: SonarObjectType;
	tagId?: number;
	viewId: number;
	viewName: string;
}

export interface QuickAccessViewsProps {
	onSelect: (viewId: QuickAccessViewSelect) => void;
}

export const QuickAccessViews: React.FC<QuickAccessViewsProps> = ({ onSelect }) => {
	const [ showAllViews, setShowAllViews ] = useBoolean(false);
	const customViews = useCustomViews();
	const apps = useDataSecurityApps();

	const getViews = useAction(coreActions.requestGetCoreDataDictionaryViewReferences);

	useOnMount(() => {
		getViews({
			count: 50,
			ordering: 'LastUsed~d'
		});
	});

	const views: Array<SearchGroup> = useMemo(() => [ {
		...viewsGroup,
		children: ((showAllViews ? customViews : customViews.slice(0, 10)) ?? [])
			.map((view) => ({
				group: SearchOptionGroup.View,
				key: view.id,
				label: <QuickAccessViewButton viewId={ view.id } />,
				value: view.id
			}))
	} ], [ showAllViews, customViews ]);

	const handleOnClick = useCallback((info: MenuInfo) => {
		const view = customViews.find((customView) => customView.id === Number(info.key));

		const sonarObjectType = view?.sonarObjectTypeId ?
			getSonarObjectTypeFromId(view.sonarObjectTypeId) : undefined;

		const displayName = view?.sonarObjectName ??
			apps.find((app) => app.applicationId === view?.applicationId)?.name;

		onSelect({
			applicationId: view?.applicationId,
			complianceCategory: view?.complianceCategory,
			displayName,
			sonarObjectId: view?.sonarObjectId,
			sonarObjectType,
			tagId: view?.tagId,
			viewId: Number(info.key),
			viewName: view?.name ?? ''
		});
	}, [ apps, customViews, onSelect ]);

	return (
		<div>
			<Menu
				className='quick-access-menu'
				items={ views }
				mode='inline'
				onClick={ handleOnClick } />
			<When condition={ !customViews.length }>
				<div className='quick-access-views-placeholder'>
					<SonarText>{ DATA_DICTIONARY_COPY.CUSTOMIZED_VIEWS_DISPLAYED_HERE }</SonarText>
					<SonarText>
						{ DATA_DICTIONARY_COPY.FOR_MORE_ON_SAVED_VIEWS }
						<a
							className='help-article-link'
							href={ LINKS.DATA_DICTIONARY_HELP_ARTICLE }
							target='_blank'>
							{ DATA_DICTIONARY_COPY.DATA_DICTIONARY_HELP_ARTICLE }
						</a>
					</SonarText>
				</div>
			</When>
			<When condition={ Number(customViews.length) > 10 }>
				<Button
					className='show-all'
					onClick={ setShowAllViews.toggle }
					type='link'>
					{ showAllViews ? COPY_GENERIC.SHOW_LESS : COPY_GENERIC.SHOW_ALL }
				</Button>
			</When>
		</div>
	);
};
